import { useState, useEffect, useRef } from 'react';
import { FiEye, FiTrash2, FiChevronUp, FiChevronDown, FiUpload } from 'react-icons/fi';
import { useLibrary } from '../context/LibraryContext';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Modal';
import { requestUploadURL, uploadToS3 } from '../utils/api';
import { calculateSHA256 } from '../utils/fileUtils';
import { useApi } from '../hooks/useApi';
import { useOpenDocument } from '../hooks/useOpenDocument';
import iconUpload from '../assets/icon-upload.png';
import iconPdf from '../assets/icon-pdf.png';
import yourFiles from '../assets/your-files.png';
import './Library.css';

// Polling intervals
const ACTIVE_POLLING_INTERVAL_MS = 10000; // 10s when processing
const IDLE_POLLING_INTERVAL_MS = 30000;   // 30s when idle (to see other users' changes)
const MAX_DELETE_RETRIES = 3;

export default function Library() {
  const { documents, removeDocument, loading, refreshDocuments, addDocument } = useLibrary();
  const { user } = useAuth();
  const { fetchAPI, triggerLibraryScan } = useApi();
  const openDocument = useOpenDocument();
  const [isScanning, setIsScanning] = useState(false);
  const [scanToast, setScanToast] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, document: null });
  const [deleteAllModal, setDeleteAllModal] = useState(false);
  const [sortColumn, setSortColumn] = useState('modifiedAt'); // Default sort by last modified
  const [sortDirection, setSortDirection] = useState('desc'); // Default descending (newest first)
  const [duplicateNotification, setDuplicateNotification] = useState(null);
  const fileInputRef = useRef(null);

  // Refresh documents when component mounts
  useEffect(() => {
    refreshDocuments();
  }, [refreshDocuments]);

  // Auto-dismiss duplicate notification after 6 seconds
  useEffect(() => {
    if (duplicateNotification) {
      const timer = setTimeout(() => {
        setDuplicateNotification(null);
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [duplicateNotification]);

  // Continuous polling for document updates (adaptive rate based on activity)
  useEffect(() => {
    const hasProcessingDocuments = documents.some(
      doc =>
        doc.status === 'uploading'
        || doc.status === 'processing'
        || doc.status === 'splitting'
        || doc.status === 'split_pending'
        || doc.status === 'deleting'
        || doc.kbStatus === 'syncing'
    );

    // Use faster polling when processing, slower when idle
    // Idle polling ensures users see other users' uploads/deletes
    const pollInterval = hasProcessingDocuments
      ? ACTIVE_POLLING_INTERVAL_MS
      : IDLE_POLLING_INTERVAL_MS;

    const intervalId = setInterval(() => {
      refreshDocuments();
    }, pollInterval);

    // Listen for manual refresh events
    const handleManualRefresh = () => {
      refreshDocuments();
    };
    window.addEventListener('refreshLibrary', handleManualRefresh);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('refreshLibrary', handleManualRefresh);
    };
  }, [documents, refreshDocuments]);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event) => {
    const selectedFiles = event.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    const fileArray = Array.from(selectedFiles);

    // Track upload results for summary
    const uploadResults = {
      successful: [],
      failed: [],
      duplicates: [],
      invalidType: [],
      tooLarge: []
    };

    // Validate file types and sizes
    const validFiles = fileArray.filter(file => {
      if (file.type !== 'application/pdf') {
        uploadResults.invalidType.push(file.name);
        return false;
      }
      if (file.size > 20 * 1024 * 1024) {
        uploadResults.tooLarge.push(file.name);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) {
      const parts = [];
      if (uploadResults.invalidType.length > 0) parts.push(`${uploadResults.invalidType.length} file(s) must be PDF format`);
      if (uploadResults.tooLarge.length > 0) parts.push(`${uploadResults.tooLarge.length} file(s) exceed the 20 MB limit`);
      setDuplicateNotification({ message: parts.join('; ') });
      return;
    }


    // Process all files concurrently — each file hashes + uploads independently
    const createdByName = user?.name || user?.email?.split('@')[0] || 'Unknown';

    const results = await Promise.all(validFiles.map(async (file) => {
      try {
        const fileHash = await calculateSHA256(file);

        let response;
        try {
          response = await requestUploadURL(fetchAPI, file, fileHash);
        } catch (error) {
          if (error.status === 409 && error.data?.duplicate === true) {
            return { type: 'duplicate', name: file.name };
          }
          throw error;
        }

        if (response.duplicate) {
          return { type: 'duplicate', name: file.name };
        }

        addDocument({
          id: response.document_id || `temp-${Date.now()}-${Math.random()}`,
          name: file.name,
          status: 'uploading',
          createdBy: createdByName,
          modifiedAt: new Date().toISOString(),
          url: null
        });

        await uploadToS3(response.upload_url, file, response.upload_fields || {});
        return { type: 'success', name: file.name };
      } catch (error) {
        return { type: 'failed', name: file.name, error: error.message };
      }
    }));

    results.forEach((r) => uploadResults[r.type === 'failed' ? 'failed' : r.type === 'duplicate' ? 'duplicates' : 'successful'].push(r.name));

    // Refresh once after all files have been processed to show real data from server
    await refreshDocuments();

    // Notify other components that library was updated (for multi-user scenarios)
    window.dispatchEvent(new Event('refreshLibrary'));

    // Show notification for duplicates
    if (uploadResults.duplicates.length > 0) {
      const fileNames = uploadResults.duplicates.length === 1
        ? uploadResults.duplicates[0]
        : `${uploadResults.duplicates.length} files`;
      setDuplicateNotification({
        message: `${fileNames} already exists in your library`,
        files: uploadResults.duplicates
      });
    }

    // Log detailed failures to console for debugging
    if (uploadResults.failed.length > 0) {
      console.log('Failed uploads:', uploadResults.failed);
    }

    // Reset file input
    event.target.value = '';
  };

  const handleDeleteClick = (document) => {
    setDeleteModal({ isOpen: true, document });
  };

  const handleDeleteAllConfirm = () => {
    setDeleteAllModal(false);
    const toDelete = [...documents];
    toDelete.forEach((doc) => performDelete(doc));
  };

  const performDelete = (documentToDelete, retryCount = 0) => {
    removeDocument(documentToDelete, (progressData) => {
      // Progress is tracked by the operations indicator
    })
      .then(() => {
        // Success - operations indicator will show completion
      })
      .catch((error) => {
        console.error('Delete operation failed:', error);

        if (retryCount >= MAX_DELETE_RETRIES) {
          // Max retries reached
          return;
        }

        // Retry deletion
        setTimeout(() => performDelete(documentToDelete, retryCount + 1), 2000);
      });
  };

  const handleDeleteConfirm = () => {
    const documentToDelete = deleteModal.document;

    if (!documentToDelete) {
      setDeleteModal({ isOpen: false, document: null });
      return;
    }

    // Close modal immediately
    setDeleteModal({ isOpen: false, document: null });

    // Start deletion in background
    performDelete(documentToDelete);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDeletionStatusLabel = (doc) => {
    if (!doc || doc.deletionProgress === undefined) {
      return 'Deleting';
    }

    if (doc.deletionProgress === 0) {
      return 'Deleting';
    } else if (doc.deletionProgress < 50) {
      return 'Syncing';
    } else if (doc.deletionProgress < 100) {
      return 'Finalizing';
    } else {
      return 'Complete';
    }
  };

  const getDeletionHelpText = (doc) => {
    if (!doc || doc.deletionProgress === undefined) {
      return null;
    }

    if (doc.deletionProgress === 0) {
      return null;
    } else if (doc.deletionProgress < 100) {
      return 'Removing from knowledge base...';
    }
    return null;
  };

  const getEffectiveKbStatus = (doc) => {
    if (!doc) return null;
    return doc.kbStatus
      || doc.kb_status
      || (doc.status === 'processed' ? 'ready' : null)
      || (doc.status === 'kb_failed' ? 'failed' : null);
  };

  const getStatusBadge = (doc) => {
    const status = doc?.status;
    const kbStatus = getEffectiveKbStatus(doc);
    const normalizedStatus = (status === 'splitting' || status === 'split_pending') ? 'processing' : status;

    const statusConfig = {
      uploading: { label: 'Uploading', className: 'status-uploading' },
      processing: { label: 'Processing', className: 'status-processing' },
      processed: { label: 'Processed', className: 'status-processed' },
      failed: { label: 'Failed', className: 'status-failed' },
      skipped: {
        label: 'Skipped',
        className: 'status-failed',
        helpText: doc?.skipReason || 'Document was skipped during processing'
      },
      'not processed': {
        label: 'Not processed',
        className: 'status-failed'
      },
      deleting: {
        label: getDeletionStatusLabel(doc),
        className: 'status-deleting',
        helpText: getDeletionHelpText(doc)
      }
    };

    let config = statusConfig[normalizedStatus];

    if (kbStatus === 'syncing') {
      config = { label: 'Processing KB', className: 'status-kb-syncing', helpText: 'Building the knowledge base...' };
    }

    if (status === 'kb_failed' || kbStatus === 'failed') {
      config = {
        label: 'KB failed',
        className: 'status-kb-failed',
        helpText: doc?.kbFailureReason || doc?.kb_failure_reason || 'Knowledge base sync failed'
      };
    }

    if (!config) {
      config = { label: normalizedStatus, className: 'status-unknown' };
    }

    const showSpinner = normalizedStatus === 'uploading'
      || normalizedStatus === 'processing'
      || normalizedStatus === 'deleting'
      || kbStatus === 'syncing';

    return (
      <div className="status-badge-container">
        <span className={`status-badge ${config.className}`}>
          {showSpinner && <span className="status-spinner"></span>}
          {config.label}
        </span>
        {config.helpText && (
          <span className="status-help-text">{config.helpText}</span>
        )}
      </div>
    );
  };

  const formatTimeRemaining = (seconds) => {
    if (!seconds) return '';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleCompareLibrary = async () => {
    const readyDocs = documents.filter((d) => d.status === 'processed' && d.policyId);
    if (readyDocs.length < 2) return;
    setIsScanning(true);
    try {
      await triggerLibraryScan(
        readyDocs.map((d) => ({ policy_id: d.policyId, filename: d.name }))
      );
      setScanToast(true);
    } catch (err) {
      console.error('Library scan failed:', err);
    } finally {
      setIsScanning(false);
    }
  };

  const getSortedDocuments = () => {
    const sorted = [...documents].sort((a, b) => {
      let aValue, bValue;

      switch (sortColumn) {
        case 'name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        case 'status':
          aValue = (a.status || '').toLowerCase();
          bValue = (b.status || '').toLowerCase();
          break;
        case 'createdBy':
          aValue = (a.createdBy || '').toLowerCase();
          bValue = (b.createdBy || '').toLowerCase();
          break;
        case 'modifiedAt':
          // Convert to timestamps for comparison
          aValue = a.modifiedAt ? new Date(a.modifiedAt).getTime() : 0;
          bValue = b.modifiedAt ? new Date(b.modifiedAt).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const sortedDocuments = getSortedDocuments();

  return (
    <div className="library-page">
      <div className="library-header">
        <div className="library-header-left">
          <img src={yourFiles} alt="Your files" className="your-files-title" />
          {documents.length > 0 && (
            <span className="document-count">{documents.length} document{documents.length !== 1 ? 's' : ''}</span>
          )}
        </div>
        <div className="library-header-actions">
          <button
            className="upload-btn"
            onClick={handleUploadClick}
            title="Upload documents"
          >
            <FiUpload className="upload-btn-icon" />
            Upload
          </button>
          {documents.filter((d) => d.status === 'processed' && d.policyId).length >= 2 && (
            <button
              className="compare-library-btn"
              onClick={handleCompareLibrary}
              disabled={isScanning}
              title="Compare all library documents"
            >
              {isScanning ? 'Starting…' : 'Compare Library'}
            </button>
          )}
          {documents.length > 0 && (
            <button
              className="delete-all-btn"
              onClick={() => setDeleteAllModal(true)}
              title="Delete all documents"
              aria-label="Delete all documents"
            >
              <FiTrash2 />
              Delete All
            </button>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </div>

      {duplicateNotification && (
        <div className="duplicate-notification">
          <div className="duplicate-notification-content">
            <span className="duplicate-notification-message">
              {duplicateNotification.message}
            </span>
            <button
              className="duplicate-notification-close"
              onClick={() => setDuplicateNotification(null)}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <div className="library-content">
        {documents.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <img src={iconUpload} alt="Upload" />
            </div>
            {loading ? (
              <>
                <h3>Loading your documents...</h3>
                <p>Please wait</p>
              </>
            ) : (
              <>
                <h3>No documents yet</h3>
                <p>Upload your first policy document to get started</p>
              </>
            )}
          </div>
        ) : (
          <div className="documents-table">
            <table className="table">
              <thead>
                <tr>
                  <th className="number-column">
                    <span className="header-label">#</span>
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('name')}>
                    <span className="header-label">
                      FILE
                      {sortColumn === 'name' && (
                        <span className="sort-icon">
                          {sortDirection === 'asc' ? <FiChevronUp /> : <FiChevronDown />}
                        </span>
                      )}
                    </span>
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('status')}>
                    <span className="header-label">
                      STATUS
                      {sortColumn === 'status' && (
                        <span className="sort-icon">
                          {sortDirection === 'asc' ? <FiChevronUp /> : <FiChevronDown />}
                        </span>
                      )}
                    </span>
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('createdBy')}>
                    <span className="header-label">
                      CREATED BY
                      {sortColumn === 'createdBy' && (
                        <span className="sort-icon">
                          {sortDirection === 'asc' ? <FiChevronUp /> : <FiChevronDown />}
                        </span>
                      )}
                    </span>
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('modifiedAt')}>
                    <span className="header-label">
                      LAST MODIFIED
                      {sortColumn === 'modifiedAt' && (
                        <span className="sort-icon">
                          {sortDirection === 'asc' ? <FiChevronUp /> : <FiChevronDown />}
                        </span>
                      )}
                    </span>
                  </th>
                  <th className="actions-header"></th>
                </tr>
              </thead>
              <tbody>
                {sortedDocuments.map((doc, index) => {
                  return (
                    <tr key={doc.id} className={doc.status === 'deleting' ? 'deleting-row' : ''}>
                      <td className="number-cell">
                        {index + 1}
                      </td>
                      <td>
                        <div className="file-cell">
                          <img src={iconPdf} alt="PDF" className="file-icon-img" />
                          <span className="file-name">{doc.name}</span>
                        </div>
                      </td>
                      <td>
                        {getStatusBadge(doc)}
                        {doc.status === 'deleting' && doc.deletionTimeRemaining && (
                          <div className="status-timestamp">
                            Est. {formatTimeRemaining(doc.deletionTimeRemaining)} remaining
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="user-cell">
                          <span className="user-name">{doc.createdBy}</span>
                        </div>
                      </td>
                      <td>{formatDateTime(doc.modifiedAt)}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="action-btn"
                            title="View"
                            onClick={() => openDocument(doc)}
                            disabled={doc.status !== 'processed'}
                          >
                            <FiEye />
                          </button>
                          <button
                            className="action-btn"
                            title="Delete"
                            onClick={() => handleDeleteClick(doc)}
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {scanToast && (
        <div className="lib-toast">
          <span className="lib-toast-icon">ⓘ</span>
          <div className="lib-toast-text">
            <strong>Library scan in progress</strong>
            <p className="lib-toast-body">We're processing your library. Results will appear in Policy Checker shortly.</p>
          </div>
          <button className="lib-toast-close" onClick={() => setScanToast(false)} aria-label="Dismiss">×</button>
        </div>
      )}

      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, document: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Document"
        message="Are you sure you want to delete this document? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        danger={true}
      />

      <Modal
        isOpen={deleteAllModal}
        onClose={() => setDeleteAllModal(false)}
        onConfirm={handleDeleteAllConfirm}
        title="Delete All Documents"
        message={`Are you sure you want to delete all ${documents.length} document${documents.length !== 1 ? 's' : ''}? This action cannot be undone.`}
        confirmText="Delete All"
        cancelText="Cancel"
        danger={true}
      />
    </div>
  );
}
